import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { BookingWidget } from "@/components/BookingWidget";
import * as Icons from "lucide-react";
import { Star } from "lucide-react";

export default function ListingDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [listing, setListing] = useState(null);

  useEffect(() => {
    api.get(`/listings/${slug}`).then(r => setListing(r.data)).catch(() => {});
  }, [slug]);

  if (!listing) return <div className="py-32 text-center text-[#8C7A6B]">Loading…</div>;
  const lang = i18n.language;
  const name = lang === "he" ? listing.name_he : listing.name_en;
  const neigh = lang === "he" ? listing.neighborhood_he : listing.neighborhood_en;
  const desc = lang === "he" ? listing.description_he : listing.description_en;
  const rules = lang === "he" ? listing.house_rules_he : listing.house_rules_en;

  return (
    <div data-testid="listing-detail" className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <p className="overline mb-2">{neigh}</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2C2A29] tracking-tight">{name}</h1>

      {/* Gallery */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-4 gap-3 h-[280px] sm:h-[520px]">
        <div className="sm:col-span-2 sm:row-span-2 rounded-2xl overflow-hidden">
          <img src={listing.images?.[0] || listing.cover_image} alt={name} className="w-full h-full object-cover" />
        </div>
        {listing.images?.slice(1, 5).map((src, i) => (
          <div key={src || `gallery-${i}`} className="rounded-2xl overflow-hidden hidden sm:block">
            <img src={src} alt={`${name} ${i}`} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16 mt-14">
        <div className="lg:col-span-2 space-y-12">
          <section>
            <h2 className="font-serif text-3xl text-[#2C2A29] mb-4">{t("detail.description")}</h2>
            <p className="text-[#595450] leading-relaxed">{desc}</p>
            <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-[#EAE3D9]">
              <Stat label={t("listings.bedrooms")} value={listing.bedrooms} />
              <Stat label={t("listings.bathrooms")} value={listing.bathrooms} />
              <Stat label={t("listings.filter_guests")} value={listing.max_guests} />
            </div>
          </section>

          <section>
            <h2 className="font-serif text-3xl text-[#2C2A29] mb-6">{t("detail.amenities")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {listing.amenities?.map((a, i) => {
                const Icon = Icons[a.icon] || Icons.Sparkles;
                return (
                  <div key={i} className="flex items-center gap-3 text-[#2C2A29]">
                    <Icon className="h-5 w-5 text-[#C86D53]" />
                    <span>{lang === "he" ? a.label_he : a.label_en}</span>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="font-serif text-3xl text-[#2C2A29] mb-6">{t("detail.location")}</h2>
            <div className="rounded-2xl overflow-hidden border border-[#EAE3D9] h-[320px]">
              <iframe
                title="map"
                width="100%"
                height="100%"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${listing.longitude-0.01}%2C${listing.latitude-0.005}%2C${listing.longitude+0.01}%2C${listing.latitude+0.005}&layer=mapnik&marker=${listing.latitude}%2C${listing.longitude}`}
              />
            </div>
          </section>

          <section>
            <h2 className="font-serif text-3xl text-[#2C2A29] mb-6">{t("detail.house_rules")}</h2>
            <ul className="space-y-2 text-[#595450]">
              {rules?.map((r, i) => <li key={i}>· {r}</li>)}
            </ul>
          </section>

          {listing.reviews?.length > 0 && (
            <section>
              <h2 className="font-serif text-3xl text-[#2C2A29] mb-6">{t("detail.reviews")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {listing.reviews.map((r, i) => {
                  const reviewKey = `${r.author}-${r.date}-${i}`;
                  return (
                  <div key={reviewKey} className="bg-white rounded-2xl p-6 border border-[#EAE3D9]">
                    <div className="flex gap-0.5 text-[#C86D53] mb-2">
                      {Array.from({ length: r.rating }).map((_, j) => <Star key={`${reviewKey}-star-${j}`} className="h-3.5 w-3.5 fill-current" />)}
                    </div>
                    <p className="font-serif text-lg text-[#2C2A29]">&ldquo;{lang === "he" ? r.text_he : r.text_en}&rdquo;</p>
                    <p className="text-xs text-[#8C7A6B] mt-3">— {r.author}, {r.date}</p>
                  </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Sticky widget */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-28">
            <BookingWidget listing={listing} />
          </div>
        </aside>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-serif text-3xl text-[#2C2A29]">{value}</div>
    <div className="text-xs uppercase tracking-wider text-[#8C7A6B] mt-1">{label}</div>
  </div>
);
